/* Skyward PM C3 — SWI construction theme */
:root {
  --ink: #1a1a1a;
  --ink-2: #2a2724;
  --orange: #c55a11;       /* fills, large text */
  --orange-text: #a3470d;  /* small orange text (AA on white) */
  --cream: #f5ede3;
  --tan: #ead6c2;
  --bg: #faf7f2;
  --card: #ffffff;
  --line: #e4dacd;
  --line-2: #efe6da;
  --muted: #5c554c;
  --muted-on-dark: #cfc4b6;
  --good: #285c36;
  --good-bg: #e2efe5;
  --warn: #7a350a;
  --warn-bg: #fbe3cf;
  --font: Arial, Helvetica, sans-serif;
}

* { box-sizing: border-box; }
html, body, #root { height: 100%; }
body {
  margin: 0;
  background: var(--bg);
  color: var(--ink);
  font-family: var(--font);
  font-size: 14px;
  line-height: 1.45;
  -webkit-font-smoothing: antialiased;
}
a { color: var(--orange-text); }
a:hover { color: #7a350a; }
:focus-visible { outline: 3px solid var(--orange); outline-offset: 2px; }
h1, h2, h3 { margin: 0; }
.muted { color: var(--muted); }
.small { font-size: 12px; }
.sr { position: absolute; left: -9999px; }

/* shell */
.shell { display: flex; min-height: 100vh; }
.side {
  width: 236px; flex: none; background: var(--ink); color: var(--cream);
  display: flex; flex-direction: column; gap: 14px; padding: 18px 12px;
  position: sticky; top: 0; height: 100vh; overflow-y: auto;
}
.brand { display: flex; align-items: center; gap: 10px; color: var(--cream); text-decoration: none; padding: 0 6px; }
.brand strong { display: block; font-size: 15px; }
.brand small { display: block; font-size: 11px; color: var(--muted-on-dark); }
.logo {
  width: 34px; height: 34px; border-radius: 8px; background: var(--orange); color: #fff;
  display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 13px; flex: none;
}
.nav-list { display: flex; flex-direction: column; gap: 2px; }
.nav-item {
  display: flex; justify-content: space-between; align-items: center; gap: 8px;
  padding: 8px 10px; border-radius: 7px; font-size: 13px; color: var(--cream); text-decoration: none;
}
.nav-item:hover { background: var(--ink-2); color: #fff; }
.nav-item.active { background: var(--orange); color: #fff; font-weight: 700; }
.nav-item.soon { color: #8f877d; cursor: default; }
.nav-item.soon:hover { background: none; color: #8f877d; }
.nav-item em { font-style: normal; font-size: 10px; border: 1px solid #4a453f; border-radius: 8px; padding: 1px 6px; }
.side-foot { margin-top: auto; font-size: 11px; color: var(--muted-on-dark); padding: 10px 6px 0; border-top: 1px solid #3a3631; }

.main { flex: 1; min-width: 0; display: flex; flex-direction: column; }
.top {
  display: flex; align-items: center; gap: 12px; padding: 10px 24px; background: #fff;
  border-bottom: 1px solid var(--line); position: sticky; top: 0; z-index: 20;
}
.menu-btn { display: none; width: 44px; height: 44px; border: 1px solid var(--line); border-radius: 8px; background: #fff; font-size: 18px; cursor: pointer; }
.search { flex: 1; display: flex; }
.search label { flex: 1; display: flex; }
.search input {
  width: 100%; height: 40px; border: 1px solid var(--line); border-radius: 8px; padding: 0 12px;
  background: var(--bg); font: 13px var(--font); color: var(--ink);
}
.content { padding: 24px; display: flex; flex-direction: column; gap: 20px; width: 100%; max-width: 1280px; }
.foot { margin-top: auto; padding: 12px 24px; font-size: 11px; color: var(--muted); border-top: 1px solid var(--line); text-align: center; }

.site-switch { position: relative; }
.site-btn {
  display: flex; flex-direction: column; align-items: flex-start; justify-content: center; gap: 1px;
  min-height: 44px; padding: 4px 14px; border: 0; border-radius: 8px; background: var(--ink); color: #fff; cursor: pointer; text-align: left;
}
.site-label { font-size: 10px; font-weight: 700; letter-spacing: 0.8px; color: #f2a66e; }
.site-name { font-size: 13px; font-weight: 700; }
.site-menu {
  position: absolute; top: calc(100% + 6px); left: 0; min-width: 300px; list-style: none; margin: 0; padding: 6px;
  background: #fff; border: 1px solid var(--line); border-radius: 10px; box-shadow: 0 8px 24px rgb(0 0 0 / 0.12); z-index: 30;
}
.site-menu button {
  width: 100%; display: flex; flex-direction: column; align-items: flex-start; gap: 2px; padding: 10px 12px;
  border: 0; border-radius: 7px; background: none; cursor: pointer; font: 13px var(--font); color: var(--ink); text-align: left;
}
.site-menu button span { font-size: 11px; color: var(--muted); }
.site-menu button:hover, .site-menu button.on { background: var(--cream); }

.user-menu { position: relative; }
.avatar {
  width: 40px; height: 40px; border-radius: 20px; border: 0; background: var(--ink); color: var(--cream);
  font-weight: 700; font-size: 13px; cursor: pointer;
}
.user-pop {
  position: absolute; right: 0; top: calc(100% + 6px); width: 260px; display: flex; flex-direction: column; gap: 8px;
  background: #fff; border: 1px solid var(--line); border-radius: 10px; padding: 14px; box-shadow: 0 8px 24px rgb(0 0 0 / 0.12); z-index: 30;
}

/* page parts */
.page-head { display: flex; flex-wrap: wrap; justify-content: space-between; align-items: flex-end; gap: 12px; }
.page-head h1 { font-size: 26px; }
.page-head .rule { height: 3px; width: 56px; background: var(--orange); margin: 6px 0; }
.page-head p { margin: 0; font-size: 13px; }
.actions { display: flex; gap: 8px; flex-wrap: wrap; }

.card { background: var(--card); border: 1px solid var(--line); border-radius: 12px; overflow: hidden; }
.card-head { display: flex; justify-content: space-between; align-items: center; gap: 10px; padding: 12px 16px; background: var(--ink); color: #fff; }
.card-head h2 { font-size: 13px; letter-spacing: 0.5px; text-transform: uppercase; }
.card-body { padding: 16px; }

.grid { display: grid; gap: 12px; grid-template-columns: repeat(auto-fill, minmax(250px, 1fr)); }
.tile { display: flex; flex-direction: column; gap: 8px; padding: 14px 16px; background: #fff; border: 1px solid var(--line); border-radius: 10px; }
.tile h3 { font-size: 15px; }
.kpis { display: grid; gap: 12px; grid-template-columns: repeat(auto-fit, minmax(170px, 1fr)); }
.kpi { padding: 14px 16px; background: #fff; border: 1px solid var(--line); border-radius: 10px; display: flex; flex-direction: column; gap: 4px; }
.kpi b { font-size: 26px; }
.kpi span { font-size: 11px; font-weight: 700; color: var(--muted); letter-spacing: 0.6px; text-transform: uppercase; }

.table-wrap { overflow-x: auto; }
table.t { width: 100%; border-collapse: collapse; font-size: 13px; }
table.t th { text-align: left; padding: 9px 12px; background: var(--tan); font-size: 11px; letter-spacing: 0.4px; text-transform: uppercase; white-space: nowrap; }
table.t td { padding: 10px 12px; border-bottom: 1px solid var(--line-2); vertical-align: top; }
table.t tr.click { cursor: pointer; }
table.t tr.click:hover td { background: var(--bg); }
table.t tr.deleted td { color: var(--muted); text-decoration: line-through; }

.btn { min-height: 38px; padding: 0 14px; border-radius: 8px; font: 700 13px var(--font); cursor: pointer; border: 1px solid transparent; }
.btn:disabled { opacity: 0.55; cursor: not-allowed; }
.btn-primary { background: var(--orange); color: #fff; }
.btn-primary:hover:not(:disabled) { background: #b04f0e; }
.btn-secondary { background: #fff; color: var(--ink); border-color: var(--ink); }
.btn-ghost { background: none; color: var(--orange-text); border-color: transparent; padding: 0 8px; }
.btn-danger { background: #fff; color: #9b2c1c; border-color: #9b2c1c; }
.icon-btn { width: 40px; height: 40px; border: 0; background: none; font-size: 24px; cursor: pointer; color: inherit; }

.badge { display: inline-block; font-size: 11px; font-weight: 700; padding: 3px 8px; border-radius: 10px; white-space: nowrap; }
.badge-neutral { background: var(--cream); color: var(--ink); }
.badge-orange { background: var(--orange-text); color: #fff; }
.badge-dark { background: var(--ink); color: var(--cream); }
.badge-good { background: var(--good-bg); color: var(--good); }
.badge-warn { background: var(--warn-bg); color: var(--warn); }

.field { display: flex; flex-direction: column; gap: 5px; }
.field-label { font-size: 12px; font-weight: 700; color: var(--muted); }
.field-hint { font-size: 11px; color: var(--muted); }
.field input, .field select, .field textarea {
  min-height: 40px; border: 1px solid #d9cbb9; border-radius: 8px; padding: 8px 10px; font: 14px var(--font); color: var(--ink); background: #fff;
}
.field textarea { min-height: 80px; resize: vertical; }
.form { display: flex; flex-direction: column; gap: 14px; }
.form-row { display: grid; gap: 12px; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); }
.check { display: flex; align-items: center; gap: 8px; min-height: 40px; }
.check input { width: 20px; height: 20px; accent-color: var(--orange); }

.empty { padding: 28px 16px; text-align: center; color: var(--muted); }
.error-box { background: #fde8e4; color: #7a1f12; border-radius: 8px; padding: 10px 12px; font-size: 13px; }
.toolbar { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
.toolbar input, .toolbar select { min-height: 38px; border: 1px solid #d9cbb9; border-radius: 8px; padding: 0 10px; font: 13px var(--font); background: #fff; }

.drawer-wrap { position: fixed; inset: 0; background: rgb(26 26 26 / 0.35); z-index: 50; display: flex; justify-content: flex-end; }
.drawer { width: min(520px, 100%); height: 100%; background: #fff; display: flex; flex-direction: column; }
.drawer-head { display: flex; justify-content: space-between; align-items: center; padding: 10px 10px 10px 18px; background: var(--ink); color: #fff; }
.drawer-head h2 { font-size: 15px; }
.drawer-body { flex: 1; overflow-y: auto; padding: 18px; }
.drawer-foot { display: flex; gap: 8px; justify-content: flex-end; flex-wrap: wrap; padding: 12px 18px; border-top: 1px solid var(--line); }

.toasts { position: fixed; bottom: 16px; left: 50%; transform: translateX(-50%); display: flex; flex-direction: column; gap: 8px; z-index: 60; width: min(480px, calc(100% - 32px)); }
.toast { display: flex; justify-content: space-between; align-items: center; gap: 12px; padding: 12px 14px; border-radius: 10px; color: #fff; font-size: 13px; box-shadow: 0 6px 20px rgb(0 0 0 / 0.2); }
.toast-ok { background: var(--ink); }
.toast-error { background: #9b2c1c; }
.toast button { background: none; border: 1px solid #fff; color: #fff; border-radius: 6px; padding: 4px 10px; font: 700 12px var(--font); cursor: pointer; }

/* auth */
.auth { min-height: 100vh; display: flex; align-items: center; justify-content: center; padding: 20px; background: var(--ink); }
.auth-card { width: min(420px, 100%); background: #fff; border-radius: 14px; padding: 26px; display: flex; flex-direction: column; gap: 16px; }
.auth-card .brand { color: var(--ink); padding: 0; }
.auth-card .brand small { color: var(--muted); }

.levels { display: grid; grid-template-columns: repeat(6, auto); gap: 4px; }
.lv-select { min-height: 32px; border: 1px solid #d9cbb9; border-radius: 6px; font: 12px var(--font); background: #fff; }

@media (max-width: 860px) {
  .side { position: fixed; left: 0; top: 0; z-index: 40; transform: translateX(-100%); transition: transform 0.2s; }
  .side.open { transform: none; box-shadow: 0 0 0 100vmax rgb(26 26 26 / 0.35); }
  .menu-btn { display: block; }
  .top { padding: 8px 12px; gap: 8px; }
  .search { display: none; }
  .site-btn { max-width: calc(100vw - 190px); }
  .site-name { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 100%; display: block; }
  .site-menu { min-width: 0; width: calc(100vw - 24px); }
  .content { padding: 16px; }
}

/* presence */
.online-btn {
  display: flex; align-items: center; gap: 8px; min-height: 44px; padding: 0 10px; border: 1px solid var(--line);
  border-radius: 22px; background: #fff; cursor: pointer; font: 12px var(--font); color: var(--ink);
}
.faces { display: flex; }
.faces .face + .face { margin-left: -8px; }
.face {
  width: 28px; height: 28px; border-radius: 14px; border: 2px solid #fff; background: var(--orange); color: #fff;
  font-size: 10px; font-weight: 700; display: inline-flex; align-items: center; justify-content: center; flex: none;
}
.face.away { background: #8f877d; }
.online-count { display: flex; align-items: center; gap: 6px; white-space: nowrap; }
.dot { width: 8px; height: 8px; border-radius: 4px; background: #b9b0a5; }
.dot.on { background: #2e8b57; }
.also-here {
  display: flex; align-items: center; gap: 10px; padding: 8px 12px; border-radius: 10px; background: var(--cream);
  font-size: 12px; color: var(--ink);
}
.banner { padding: 10px 12px; border-radius: 8px; font-size: 13px; display: flex; gap: 10px; align-items: center; justify-content: space-between; flex-wrap: wrap; }
.banner-info { background: var(--cream); }
.banner-warn { background: var(--warn-bg); color: var(--warn); }
@media (max-width: 860px) { .online-count { display: none; } .online-btn { padding: 0 6px; } }

/* registers (V0.2) */
.multi { display: flex; flex-wrap: wrap; gap: 6px; }
.chip {
  min-height: 32px; padding: 0 10px; border: 1px solid #d9cbb9; border-radius: 16px; background: #fff;
  font: 12px var(--font); color: var(--ink); cursor: pointer;
}
.chip.on { background: var(--ink); border-color: var(--ink); color: #fff; }
.chip:disabled { cursor: default; opacity: 0.8; }
.num { font-weight: 700; white-space: nowrap; }
.clip { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
.overdue { color: #9b2c1c; font-weight: 700; }
.right { text-align: right; white-space: nowrap; }
.count-line { font-size: 12px; color: var(--muted); padding: 8px 16px; border-top: 1px solid var(--line-2); }
.item-layout { display: grid; grid-template-columns: minmax(0, 1.5fr) minmax(0, 1fr); gap: 16px; align-items: start; }
.stack { display: flex; flex-direction: column; gap: 16px; min-width: 0; }
@media (max-width: 1100px) { .item-layout { grid-template-columns: minmax(0, 1fr); } }
.facts { display: grid; grid-template-columns: repeat(auto-fill, minmax(170px, 1fr)); gap: 12px 16px; margin: 0; }
.facts div { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.facts dt { font-size: 11px; font-weight: 700; color: var(--muted); text-transform: uppercase; letter-spacing: 0.4px; }
.facts dd { margin: 0; white-space: pre-wrap; overflow-wrap: anywhere; }
.facts .wide { grid-column: 1 / -1; }
.item-head { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; }
.back { font-size: 13px; text-decoration: none; }

/* comments */
.thread { border-bottom: 1px solid var(--line-2); padding: 12px 0; }
.thread:first-child { padding-top: 0; }
.cmt { display: flex; gap: 10px; }
.cmt .face { width: 30px; height: 30px; border-radius: 15px; border: 0; }
.cmt-body { flex: 1; min-width: 0; }
.cmt-head { display: flex; flex-wrap: wrap; gap: 6px; align-items: baseline; font-size: 12px; }
.cmt-head strong { font-size: 13px; }
.cmt-text { white-space: pre-wrap; overflow-wrap: anywhere; margin: 4px 0; }
.cmt-actions { display: flex; gap: 12px; flex-wrap: wrap; font-size: 12px; }
.replies { margin: 10px 0 0 20px; display: flex; flex-direction: column; gap: 10px; padding-left: 10px; border-left: 2px solid var(--line-2); }
.mention { background: var(--cream); color: var(--orange-text); font-weight: 700; border-radius: 4px; padding: 0 3px; }
.composer { display: flex; flex-direction: column; gap: 8px; position: relative; }
.composer textarea {
  min-height: 70px; border: 1px solid #d9cbb9; border-radius: 8px; padding: 8px 10px; font: 14px var(--font); resize: vertical; width: 100%;
}
.composer-row { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; justify-content: space-between; }
.suggest {
  position: absolute; left: 0; top: 100%; z-index: 5; background: #fff; border: 1px solid var(--line); border-radius: 8px;
  box-shadow: 0 8px 24px rgb(0 0 0 / 0.12); min-width: 240px; max-height: 240px; overflow: auto;
}
.suggest button { display: block; width: 100%; text-align: left; padding: 8px 10px; border: 0; background: none; font: 13px var(--font); cursor: pointer; }
.suggest button.on, .suggest button:hover { background: var(--cream); }
.linkbtn { background: none; border: 0; padding: 0; min-height: 24px; color: var(--orange-text); font: inherit; cursor: pointer; text-decoration: underline; }
.linkbtn.danger { color: #9b2c1c; }

/* files with versions */
.doc { display: flex; gap: 10px; align-items: flex-start; padding: 10px 0; border-bottom: 1px solid var(--line-2); }
.doc:last-child { border-bottom: 0; }
.doc-ico { width: 34px; height: 34px; border-radius: 6px; background: var(--tan); display: flex; align-items: center; justify-content: center; font-size: 10px; font-weight: 700; flex: none; }
.doc-main { flex: 1; min-width: 0; }
.doc-name { font-weight: 700; overflow-wrap: anywhere; }
.versions { margin: 6px 0 0; padding: 0; list-style: none; font-size: 12px; }
.versions li { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; padding: 3px 0; }
.drop { border: 2px dashed #d9cbb9; border-radius: 10px; padding: 14px; text-align: center; color: var(--muted); font-size: 13px; }
.drop.over { border-color: var(--orange); background: var(--cream); }

/* import admin */
.progress { height: 8px; background: var(--line-2); border-radius: 4px; overflow: hidden; }
.progress > span { display: block; height: 100%; background: var(--orange); }
.report { font-size: 13px; display: flex; flex-direction: column; gap: 8px; }
.report ul { margin: 0; padding-left: 18px; }

/* in-page document viewer */
.viewer { position: fixed; inset: 0; z-index: 70; background: rgb(26 26 26 / 0.96); display: flex; flex-direction: column; }
.viewer-head { display: flex; align-items: center; gap: 10px; padding: 6px 8px 6px 16px; background: var(--ink); color: #fff; flex-wrap: wrap; }
.viewer-name { flex: 1; min-width: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; font-size: 14px; }
.viewer-nav, .viewer-actions { display: flex; align-items: center; gap: 4px; }
.viewer .btn-ghost { color: #fff; }
.viewer-link { display: inline-flex; align-items: center; text-decoration: none; }
.viewer-body { flex: 1; min-height: 0; display: flex; align-items: center; justify-content: center; padding: 12px; }
.viewer-frame { width: 100%; height: 100%; border: 0; background: #fff; border-radius: 6px; }
.viewer-img { max-width: 100%; max-height: 100%; object-fit: contain; background: #fff; border-radius: 4px; }
.viewer-msg { color: #fff; font-size: 14px; text-align: center; padding: 16px; }
@media (max-width: 860px) {
  .viewer-body { padding: 0; } .viewer-frame { border-radius: 0; }
  .viewer-head { padding: 8px 6px 4px 12px; row-gap: 0; }
  .viewer-name { flex-basis: 100%; order: -1; white-space: normal; }
  .viewer-actions { margin-left: auto; }
}
